import type { FetchLike } from "@emdur/ui";
import type { CapabilityItem } from "./clients";
import { diffClients, manifestJson } from "./clients";
import { patchVersioned, readVersioned } from "./fetcher";
import validateRole from "./roleContract.generated";
import type { Proposal } from "./proposals";
import type { Patch } from "./mergePatch";
import { permissionTitle } from "./permissionCopy";

export interface RoleCreate {
  key: string;
  name: string;
  description?: string;
  capabilities: string[];
}
export interface Role extends RoleCreate {
  system: boolean;
  all_capabilities?: boolean;
  created_at: string;
  updated_at: string;
}
export const rolesPath = (org: string) =>
  `/v1/organizations/${encodeURIComponent(org)}/roles`;
export const rolePath = (org: string, key: string) =>
  `${rolesPath(org)}/${encodeURIComponent(key)}`;
export const roleHref = (org: string, key?: string) =>
  `/orgs/${encodeURIComponent(org)}/roles${key ? `/${encodeURIComponent(key)}` : ""}`;
export function requireAssignable(
  permissions: string[],
  catalogue: CapabilityItem[],
) {
  for (const permission of permissions) {
    const item = catalogue.find((entry) => entry.capability === permission);
    if (!item)
      throw new Error(
        "A permission this console doesn't recognise yet cannot be added to a role. Ask the manifest's author to check it.",
      );
    if (item.assignable !== true)
      throw new Error(
        `${permissionTitle(item.title)} is personal access. Organisation roles cannot hold it. Remove it from the manifest.`,
      );
  }
}
export function parseRoleManifest(
  text: string,
  catalogue: CapabilityItem[],
): RoleCreate {
  const value = manifestJson(text);
  if (!validateRole(value))
    throw new Error(
      "This role manifest needs a key, a name and at least one permission. Check the RoleCreate format and list each permission once.",
    );
  const role = value as RoleCreate;
  if (role.key === "admin")
    throw new Error(
      "Admin is the built-in role. Choose a different key for this role.",
    );
  requireAssignable(role.capabilities, catalogue);
  return role;
}
export const diffPermissions = (before: string[], after: string[]) =>
  diffClients(
    { client_name: "", grant_types: [], capabilities: before },
    { client_name: "", grant_types: [], capabilities: after },
  );
export const readRole = (fetch: FetchLike, org: string, key: string) =>
  readVersioned<Role>(fetch, rolePath(org, key), "role");
export async function updateRole(
  fetch: FetchLike,
  org: string,
  role: string,
  patch: Patch,
  etag: string,
  key: string,
) {
  const answer = await patchVersioned<Role | Proposal>(
    fetch,
    rolePath(org, role),
    patch,
    etag,
    key,
  );
  return answer.status === 202
    ? { kind: "proposal" as const, proposal: answer.data as Proposal }
    : { kind: "role" as const, role: answer.data as Role, etag: answer.etag };
}
